import { useState, useEffect } from 'react';
import { api } from '../api';
import { styles } from '../styles';
import { BackIcon, Spinner } from '../components/Icons';

export default function AlertSettingsScreen({ onBack }) {
  const [thresholds, setThresholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get('/notifications/thresholds')
      .then(setThresholds)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handlePercentChange = (categoryId, value) => {
    setThresholds(prev =>
      prev.map(t => t.category_id === categoryId ? { ...t, percent: value } : t)
    );
  };

  const handleToggle = async (categoryId, isActive) => {
    setSaving(categoryId);
    try {
      await api.patch(`/notifications/thresholds/${categoryId}`, { is_active: isActive });
      setThresholds(prev =>
        prev.map(t => t.category_id === categoryId ? { ...t, is_active: isActive } : t)
      );
    } catch (e) {
      console.error('Failed to update threshold', e);
    } finally {
      setSaving(null);
    }
  };

  const handleSave = async (categoryId, percent) => {
    setSaving(categoryId);
    try {
      await api.patch(`/notifications/thresholds/${categoryId}`, { percent: parseInt(percent) });
    } catch (e) {
      console.error('Failed to update threshold', e);
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div style={{...styles.screen, ...styles.centered}}>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>
          <BackIcon />
        </button>
        <h1 style={styles.headerTitle}>Alert Settings</h1>
        <div style={{ width: 20 }} />
      </header>

      <p style={alertStyles.description}>
        Get notified when spending reaches a percentage of your budget.
      </p>

      <div style={alertStyles.list}>
        {thresholds.map(t => (
          <div key={t.category_id} style={alertStyles.item}>
            <div style={alertStyles.itemLeft}>
              <span style={alertStyles.categoryName}>{t.category_name}</span>
              <div style={alertStyles.percentRow}>
                <span style={alertStyles.percentLabel}>Alert at</span>
                <input
                  type="number"
                  min="1"
                  max="200"
                  style={alertStyles.percentInput}
                  value={t.percent}
                  onChange={(e) => handlePercentChange(t.category_id, e.target.value)}
                  onBlur={(e) => handleSave(t.category_id, e.target.value)}
                  disabled={!t.is_active}
                />
                <span style={alertStyles.percentLabel}>%</span>
                {saving === t.category_id && <span style={alertStyles.saving}>...</span>}
              </div>
            </div>
            <button
              style={{
                ...alertStyles.toggle,
                backgroundColor: t.is_active ? '#6366f1' : '#e2e8f0',
              }}
              onClick={() => handleToggle(t.category_id, !t.is_active)}
            >
              <div style={{
                ...alertStyles.toggleKnob,
                transform: t.is_active ? 'translateX(20px)' : 'translateX(0)',
              }} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const alertStyles = {
  description: {
    fontSize: 14,
    color: '#64748b',
    marginBottom: 20,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: 'white',
    padding: 14,
    borderRadius: 12,
    border: '1px solid #e2e8f0',
  },
  itemLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  categoryName: {
    fontSize: 15,
    fontWeight: 500,
    color: '#1e293b',
  },
  percentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  percentLabel: {
    fontSize: 13,
    color: '#64748b',
  },
  percentInput: {
    width: 50,
    padding: '4px 6px',
    fontSize: 14,
    borderRadius: 6,
    border: '1px solid #e2e8f0',
    textAlign: 'center',
  },
  saving: {
    fontSize: 12,
    color: '#94a3b8',
    marginLeft: 4,
  },
  toggle: {
    width: 48,
    height: 28,
    borderRadius: 14,
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background-color 0.2s',
  },
  toggleKnob: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'white',
    position: 'absolute',
    top: 2,
    left: 2,
    transition: 'transform 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
};